import { useMemo } from 'react';
import { format } from 'date-fns';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import styled from 'styled-components';

import {
  MobileSubtitle,
  SectionCard,
  SectionHeader,
  SectionTitle,
  TitleColumn,
  TitleGroup,
} from 'components/insights/careTransitions/shared/tableStyles';
import { Metrics } from 'components/insights/constants';
import { formatWholeNumber } from 'components/insights/formatting';
import CircularProgress from 'components/shared/CircularProgress';
import DateRangeBadge from 'components/shared/DateRangeBadge';
import DesktopOnly from 'components/shared/responsiveness/DesktopOnly';
import MobileOnly from 'components/shared/responsiveness/MobileOnly';
import { END_DATE, START_DATE } from 'constants/filterKeysConstants';
import { useBusinessDevelopmentStore } from 'stores/businessDevelopmentStore';
import { colors } from 'styles/theme/colors';
import ChartIconLarge from 'svg/ChartIconLarge';

import { SharedAnalytics } from '../BusinessDevelopmentDashboard';
import { TABLE_PIVOT, TABLE_PIVOT_OPTIONS, VIEW_BY, VIEW_BY_OPTIONS } from '../constants';

type Props = {
  data: SharedAnalytics;
  chartMetric: Metrics;
};

export default function BreakoutChart({ data, chartMetric }: Props) {
  const viewBy = useBusinessDevelopmentStore((state) => state.viewBy);
  const tablePivot = useBusinessDevelopmentStore((state) => state.tablePivot);
  const groupTypeName = useBusinessDevelopmentStore((state) => state.selectedGroupType!.displayName);
  const startDate = useBusinessDevelopmentStore((state) => state.filters[START_DATE]) as Date;
  const endDate = useBusinessDevelopmentStore((state) => state.filters[END_DATE]) as Date;

  const { loading, rollups } = data;
  const viewByLabel = VIEW_BY_OPTIONS.find((o) => o.value === viewBy)!.label;
  const pivotLabel = TABLE_PIVOT_OPTIONS.find((o) => o.value === tablePivot)!.label;
  const seriesName = tablePivot === TABLE_PIVOT.REFERRALS ? 'Total Referrals' : `${groupTypeName} ${pivotLabel}`;

  const entityGrouping = rollups.groupings[1];
  const hasData = (entityGrouping?.dimensions.getCurrentDimensionValuesByIndex(0) ?? []).length > 0;

  const chartOptions = useMemo((): Highcharts.Options => {
    const entityNames = entityGrouping?.dimensions.getCurrentDimensionValuesByIndex(0) ?? [];
    const values = entityGrouping?.metrics.getCurrentSeries(chartMetric) ?? [];

    const scrollableMinWidth = (40 + 40 * 1.5) * entityNames.length;

    return {
      chart: {
        type: 'column',
        style: { fontFamily: 'Lato' },
        height: 280,
        scrollablePlotArea: { minWidth: scrollableMinWidth, scrollPositionX: 0 },
      },
      accessibility: { enabled: false },
      credits: { enabled: false },
      title: { text: undefined },
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        symbolRadius: 6,
        itemStyle: { fontSize: '12px', fontWeight: 'normal', color: colors.black75 },
      },
      xAxis: {
        categories: entityNames,
        lineColor: colors.black10,
        tickLength: 0,
        labels: {
          style: { fontSize: '11px', color: colors.black75 },
          rotation: 0,
        },
      },
      yAxis: {
        title: { text: undefined },
        gridLineColor: colors.black10,
        labels: { style: { fontSize: '11px', color: colors.black50 } },
      },
      tooltip: {
        backgroundColor: colors.chartTooltipBackground,
        style: { color: colors.white },
        padding: 12,
        useHTML: true,
        formatter: function () {
          return `
            <b style="font-size: 12px; font-weight: bold;">${this.x}</b>
            <br />
            <div style="padding-top: 4px;">
              <span style="font-size: 12px;">${formatWholeNumber(this.y ?? 0)}</span>
            </div>`;
        },
      },
      plotOptions: {
        column: {
          borderRadius: { radius: 3, scope: 'point' as const, where: 'end' as const },
          pointWidth: 40,
        },
        series: { animation: false },
      },
      series: [
        {
          type: 'column',
          name: seriesName,
          data: values,
          color: viewBy === VIEW_BY.PAYER_SOURCE ? colors.accentGreen75 : colors.primaryBlue,
        },
      ],
    };
  }, [entityGrouping, chartMetric, seriesName, viewBy]);

  return (
    <SectionCard>
      <SectionHeader>
        <TitleGroup>
          <TitleColumn>
            <SectionTitle>{viewByLabel} Breakout</SectionTitle>
            <MobileOnly>
              <MobileSubtitle>
                {format(startDate, 'MM/dd/yyyy')} - {format(endDate, 'MM/dd/yyyy')}
              </MobileSubtitle>
            </MobileOnly>
          </TitleColumn>
          <DesktopOnly>
            <DateRangeBadge startDate={startDate} endDate={endDate} />
          </DesktopOnly>
        </TitleGroup>
      </SectionHeader>
      <ChartBody>
        {loading ? (
          <SpinnerContainer>
            <CircularProgress color={colors.primaryBlue} />
          </SpinnerContainer>
        ) : hasData ? (
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        ) : (
          <EmptyState>
            <ChartIconLarge color='#3253EF' />
            <EmptyText>No data to show for the selected date range or filters</EmptyText>
          </EmptyState>
        )}
      </ChartBody>
    </SectionCard>
  );
}

const ChartBody = styled.div`
  padding: 0 24px 24px;
  min-height: 280px;

  /* scrollablePlotArea draws a white mask on both left and right sides (single path).
     Keep the left mask (y-axis background) but clip away the right one. */
  .highcharts-scrollable-mask {
    clip-path: inset(0 50% 0 0);
  }

  .highcharts-scrolling {
    &::-webkit-scrollbar {
      height: 0.5em;
      width: 0.5em;
      -webkit-appearance: none;
    }
    &::-webkit-scrollbar-thumb {
      background-color: ${({ theme }) => theme.colors.scrollbarGray};
      border-radius: 5pt;
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  min-height: 280px;
`;

const EmptyText = styled.p`
  color: ${({ theme }) => theme.colors.black50};
  font-size: 12px;
  margin-top: 16px;
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 280px;
`;
